import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  IonIcon,
  useIonRouter,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  isPlatform,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { Group } from "@visx/group";
import { LinePath } from "@visx/shape";
import { scaleLinear } from "@visx/scale";
import { GridRows, GridColumns } from "@visx/grid";
import { AxisLeft, AxisBottom, AxisRight } from "@visx/axis";
import { curveLinear } from "@visx/curve";
import {
  DataPoint,
  GraphType,
  RequestMessageType,
} from "../../../contexts/types";
import { useBLE } from "../../../hooks/useBle";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { IS_WEB_BUILD } from "../../../utils/constants";

// Configuration - now dynamic
const MIN_BRUSH_SELECTION = 100; // 100ms minimum for dragging
const INITIAL_CLICK_SELECTION = 200; // 200ms for initial click selection
const DATA_GAP_THRESHOLD_MS = 500; // Gap threshold for segmentation
// const PLX_DISPLAY_MODE: "spO2" | "pulseRate" = "pulseRate"; // Change this to switch between SpO2 and Pulse Rate
const TIME_WINDOW_OPTIONS = [
  { label: "6s", value: 6000 },
  { label: "12s", value: 12000 },
  { label: "24s", value: 24000 },
  { label: "30s", value: 30000 },
];

// Add these PLX configuration constants
const PLX_CONFIG = {
  spO2: {
    color: "#A78BFA", // purple
    unit: "%",
    label: "SpO₂ %",
  },
  pulseRate: {
    color: "#34D399", // Light Green
    unit: "BPM",
    label: "BPM",
  },
} as const;

// PLX fixed ranges for live mode
const PLX_FIXED_RANGES = {
  spO2: [80, 100] as [number, number],
  pulseRate: [40, 160] as [number, number],
} as const;

// Live Display Buffer Interface
interface LiveDisplayPoint extends DataPoint {
  displayTime: number; // Normalized time within window (0 to timeWindow)
  isRecent: boolean; // For color differentiation
  cycleStartTime: number; // Which cycle this point belongs to
}

interface LiveDisplayBuffer {
  data: LiveDisplayPoint[];
  windowSize: number;
  lastUpdateTime: number;
  currentCycle: number;
}

// Utility function for data segmentation
const segmentDataByGaps = (
  data: DataPoint[],
  maxGapMs: number = DATA_GAP_THRESHOLD_MS
) => {
  if (!data || data.length <= 1) return [data];

  const segments: DataPoint[][] = [];
  let currentSegment: DataPoint[] = [data[0]];

  for (let i = 1; i < data.length; i++) {
    const timeDiff = data[i].time - data[i - 1].time;

    if (timeDiff <= maxGapMs) {
      currentSegment.push(data[i]);
    } else {
      // Gap detected - start new segment
      segments.push(currentSegment);
      currentSegment = [data[i]];
    }
  }

  segments.push(currentSegment);
  return segments;
};

const segmentPlxLiveDataByRecency = (
  data: (DataPoint & { isRecent?: boolean })[]
) => {
  if (!data || data.length === 0) return [];
  const segments: (typeof data)[] = [];
  let currentSegment: typeof data = [data[0]];
  let currentFlag = data[0].isRecent;

  for (let i = 1; i < data.length; i++) {
    if (data[i].isRecent !== currentFlag) {
      // break segment when recency flips
      segments.push(currentSegment);
      currentSegment = [data[i]];
      currentFlag = data[i].isRecent;
    } else {
      currentSegment.push(data[i]);
    }
  }

  segments.push(currentSegment);
  return segments;
};

// Custom Checkbox Component with Color Props
const CustomCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  uncheckedColor?: string;
}> = ({
  label,
  checked,
  onChange,
  disabled = false,
  primaryColor = "#01B7C4", // Default teal
  secondaryColor = "#00ACD7", // Default teal
  uncheckedColor = "#B0B4B7", // Default gray
}) => {
  const labelId = label.toLowerCase().replace(/\s+/g, "_");
  const gradientId = `gradient_${labelId}_${primaryColor.replace("#", "")}`;
  const clipPathId = `clip_${labelId}_${primaryColor.replace("#", "")}`;

  return (
    <button
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: "0px",
        gap: "6px",
        height: "20px",
        background: "transparent",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        flex: "none",
        opacity: disabled ? 0.5 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <div
        style={{
          width: "20px",
          height: "20px",
          position: "relative",
          flex: "none",
        }}
      >
        {checked ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <g clipPath={`url(#${clipPathId})`}>
              <path
                d="M15.8333 2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.7583 17.5 17.5 16.75 17.5 15.8333V4.16667C17.5 3.25 16.7583 2.5 15.8333 2.5ZM8.33333 14.1667L4.16667 10L5.34167 8.825L8.33333 11.8083L14.6583 5.48333L15.8333 6.66667L8.33333 14.1667Z"
                fill={`url(#${gradientId})`}
              />
            </g>
            <defs>
              <linearGradient
                id={gradientId}
                x1="2.5"
                y1="10.8851"
                x2="17.5"
                y2="10.8851"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor={primaryColor} />
                <stop offset="1" stopColor={secondaryColor} />
              </linearGradient>
              <clipPath id={clipPathId}>
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4.16667 17.5C3.70833 17.5 3.31583 17.3369 2.98917 17.0108C2.66306 16.6842 2.5 16.2917 2.5 15.8333V4.16667C2.5 3.70833 2.66306 3.31583 2.98917 2.98917C3.31583 2.66306 3.70833 2.5 4.16667 2.5H15.8333C16.2917 2.5 16.6842 2.66306 17.0108 2.98917C17.3369 3.31583 17.5 3.70833 17.5 4.16667V15.8333C17.5 16.2917 17.3369 16.6842 17.0108 17.0108C16.6842 17.3369 16.2917 17.5 15.8333 17.5H4.16667ZM4.16667 15.8333H15.8333V4.16667H4.16667V15.8333Z"
              fill={uncheckedColor}
            />
          </svg>
        )}
      </div>
      <span
        style={{
          fontFamily: "Figtree, sans-serif",
          fontStyle: "normal",
          fontWeight: "600",
          fontSize: "13px",
          lineHeight: "20px",
          display: "flex",
          alignItems: "center",
          background: checked
            ? `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
            : "unset",
          WebkitBackgroundClip: checked ? "text" : "unset",
          WebkitTextFillColor: checked ? "transparent" : uncheckedColor,
          backgroundClip: checked ? "text" : "unset",
          color: checked ? "transparent" : uncheckedColor,
          flex: "none",
        }}
      >
        {label}
      </span>
    </button>
  );
};

// Updated Time Window Selector Component with Orange Colors
const TimeWindowSelector: React.FC<{
  selectedWindow: number;
  onWindowChange: (window: number) => void;
}> = ({ selectedWindow, onWindowChange }) => {
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      {TIME_WINDOW_OPTIONS.map((option) => (
        <CustomCheckbox
          key={option.value}
          label={option.label}
          checked={selectedWindow === option.value}
          onChange={() => onWindowChange(option.value)}
          disabled={false}
          primaryColor="#FF8C00"
          secondaryColor="#FFA500"
          uncheckedColor="#B0B4B7"
        />
      ))}
    </div>
  );
};

// Enhanced Brush Component with dynamic data source and segmentation
const BrushComponent: React.FC<{
  data: DataPoint[];
  brushRange: [number, number] | null;
  onBrushChange: (range: [number, number] | null) => void;
  allTimeRange: [number, number];
  timeWindow: number;
  dataType?: string;
  dataColor?: string;
  /** Width of the graph's plotted area (excludes axis margins). */
  width: number;
  /** Left axis margin, so the brush starts where the curve starts. */
  offsetLeft: number;
}> = ({
  data,
  brushRange,
  onBrushChange,
  allTimeRange,
  timeWindow,
  dataType = "pressure",
  dataColor = "#22D3EE",
  width,
  offsetLeft,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<"start" | "end" | "area" | null>(
    null
  );
  const [dragStartPos, setDragStartPos] = useState<{
    x: number;
    initialRange: [number, number];
  } | null>(null);
  const [sliderWidth, setSliderWidth] = useState(346); // Default width

  useEffect(() => {
    const updateWidth = () => {
      if (sliderRef.current) {
        setSliderWidth(sliderRef.current.offsetWidth || 346);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const [minTime, maxTime] = allTimeRange;
  const timeSpan = maxTime - minTime;
  const displayRange: [number, number] = brushRange || [minTime, maxTime];

  const handleMouseDown =
    (type: "start" | "end" | "area") => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(type);
      if (type === "area" && brushRange) {
        setDragStartPos({
          x: e.clientX,
          initialRange: [...brushRange],
        });
      }
    };

  const handleTouchStart =
    (type: "start" | "end" | "area") => (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(type);
      if (type === "area" && brushRange) {
        const touch = e.touches[0] || e.changedTouches[0];
        setDragStartPos({
          x: touch.clientX,
          initialRange: [...brushRange],
        });
      }
    };

  const handleDoubleClick = () => {
    onBrushChange(null);
  };

  const getEventX = (e: MouseEvent | TouchEvent): number => {
    if ("touches" in e) {
      return e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
    }
    return e.clientX;
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const rect = sliderRef.current.getBoundingClientRect();
    const x = getEventX(e) - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = minTime + percentage * timeSpan;

    if (isDragging === "area" && brushRange && dragStartPos) {
      const deltaX = getEventX(e) - dragStartPos.x;
      const deltaTime = (deltaX / rect.width) * timeSpan;
      const rangeWidth =
        dragStartPos.initialRange[1] - dragStartPos.initialRange[0];
      let newStart = dragStartPos.initialRange[0] + deltaTime;
      let newEnd = dragStartPos.initialRange[1] + deltaTime;
      if (newStart < minTime) {
        newStart = minTime;
        newEnd = newStart + rangeWidth;
      }
      if (newEnd > maxTime) {
        newEnd = maxTime;
        newStart = newEnd - rangeWidth;
      }
      onBrushChange([newStart, newEnd]);
    } else if (brushRange) {
      const minSeparation = Math.max(MIN_BRUSH_SELECTION, timeSpan * 0.02);
      if (isDragging === "start") {
        const maxAllowedStart = brushRange[1] - minSeparation;
        const constrainedTime = Math.min(newTime, maxAllowedStart);
        onBrushChange([constrainedTime, brushRange[1]]);
      } else if (isDragging === "end") {
        const minAllowedEnd = brushRange[0] + minSeparation;
        const constrainedTime = Math.max(newTime, minAllowedEnd);
        onBrushChange([brushRange[0], constrainedTime]);
      }
    } else {
      const halfMinSelection = MIN_BRUSH_SELECTION / 2;
      const newStart = Math.max(minTime, newTime - halfMinSelection);
      const newEnd = Math.min(maxTime, newTime + halfMinSelection);
      onBrushChange([newStart, newEnd]);
    }
  };

  const handleEnd = () => {
    setIsDragging(null);
    setDragStartPos(null);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleMove(e);
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        handleMove(e);
      };
      const handleMouseUp = () => handleEnd();
      const handleTouchEnd = () => handleEnd();

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, brushRange, minTime, maxTime, timeSpan]);

  const startPercentage =
    timeSpan > 0 ? ((displayRange[0] - minTime) / timeSpan) * 100 : 0;
  const endPercentage =
    timeSpan > 0 ? ((displayRange[1] - minTime) / timeSpan) * 100 : 100;

  const getScaledRange = () => {
    if (data.length === 0) return [-50, 50];
    const values = data.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max((max - min) * 0.1, 1);
    return [min - padding, max + padding];
  };

  const [minValue, maxValue] = getScaledRange();

  return (
    <div
      style={{
        // Matches the graph's plot area; flexShrink:0 keeps the alignment.
        width: `${width}px`,
        marginLeft: `${offsetLeft}px`,
        flexShrink: 0,
        height: "46px",
        background: "#000000",
        borderRadius: "8px",
        position: "relative",
        marginTop: "16px",
      }}
    >
      {/* Main slider track */}
      <div
        ref={sliderRef}
        style={{
          position: "absolute",
          width: "100%",
          height: "30px",
          left: "0px",
          top: "8px",
          background: "#000000",
          borderRadius: "4px",
          overflow: "hidden",
          cursor: brushRange ? "default" : "crosshair",
        }}
        onDoubleClick={handleDoubleClick}
        onMouseDown={
          brushRange
            ? undefined
            : (e) => {
                const rect = sliderRef.current?.getBoundingClientRect();
                if (rect) {
                  const x = e.clientX - rect.left;
                  const percentage = x / rect.width;
                  const clickTime = minTime + percentage * timeSpan;
                  const halfClickSelection = INITIAL_CLICK_SELECTION / 2;
                  const newStart = Math.max(
                    minTime,
                    clickTime - halfClickSelection
                  );
                  const newEnd = Math.min(
                    maxTime,
                    clickTime + halfClickSelection
                  );
                  onBrushChange([newStart, newEnd]);
                }
              }
        }
        onTouchStart={
          brushRange
            ? undefined
            : (e) => {
                const rect = sliderRef.current?.getBoundingClientRect();
                if (rect) {
                  const touch = e.touches[0] || e.changedTouches[0];
                  const x = touch.clientX - rect.left;
                  const percentage = x / rect.width;
                  const clickTime = minTime + percentage * timeSpan;
                  const halfClickSelection = INITIAL_CLICK_SELECTION / 2;
                  const newStart = Math.max(
                    minTime,
                    clickTime - halfClickSelection
                  );
                  const newEnd = Math.min(
                    maxTime,
                    clickTime + halfClickSelection
                  );
                  onBrushChange([newStart, newEnd]);
                }
              }
        }
      >
        {/* Mini pressure graph with segmentation using visx LinePath */}
        <svg
          style={{
            pointerEvents: "none",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          viewBox={`0 0 ${sliderWidth} 30`}
        >
          <Group>
            {(() => {
              // Create brush-specific scales
              const brushXScale = scaleLinear({
                range: [0, sliderWidth],
                domain: [minTime, maxTime],
              });

              const brushYScale = scaleLinear({
                range: [30, 0], // Inverted for SVG coordinate system
                domain: [minValue, maxValue],
              });

              // Filter and segment data
              const filteredData = data.filter(
                (point) => point.time >= minTime && point.time <= maxTime
              );

              if (filteredData.length <= 1) return null;

              const segments = segmentDataByGaps(filteredData);

              return segments.map((segment, segmentIndex) => (
                <LinePath
                  key={`brush-segment-${segmentIndex}`}
                  data={segment}
                  x={(d) => brushXScale(d.time)}
                  y={(d) => brushYScale(d.value)}
                  stroke={dataColor}
                  strokeWidth="1.5"
                  strokeOpacity="0.8"
                  curve={curveLinear}
                />
              ));
            })()}
          </Group>
        </svg>

        {/* Selection overlay */}
        {brushRange && (
          <div
            style={{
              position: "absolute",
              top: "0",
              bottom: "0",
              left: `${startPercentage}%`,
              width: `${endPercentage - startPercentage}%`,
              background: "rgba(255, 255, 255, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              cursor: "move",
              pointerEvents: "auto",
            }}
            onMouseDown={handleMouseDown("area")}
            onTouchStart={handleTouchStart("area")}
          />
        )}
      </div>

      {/* Selection handles */}
      {brushRange && (
        <>
          {/* Start handle */}
          <div
            style={{
              position: "absolute",
              width: "44px",
              height: "46px",
              left: `${startPercentage}%`,
              top: "0px",
              cursor: "ew-resize",
              zIndex: 10,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "none",
              background:
                isDragging === "start"
                  ? "rgba(255,255,255,0.1)"
                  : "transparent",
              borderRadius: "8px",
              transition: "background 0.2s ease",
            }}
            onMouseDown={handleMouseDown("start")}
            onTouchStart={handleTouchStart("start")}
          >
            <div
              style={{
                width: "4px",
                height: "12px",
                background: "#FFFFFF",
                borderRadius: "2px",
                boxShadow:
                  isDragging === "start"
                    ? "0 0 8px rgba(255, 255, 255, 0.8)"
                    : "0 0 2px rgba(0, 0, 0, 0.3)",
                transition: "box-shadow 0.1s ease",
              }}
            />
          </div>

          {/* End handle */}
          <div
            style={{
              position: "absolute",
              width: "44px",
              height: "46px",
              left: `${endPercentage}%`,
              top: "0px",
              cursor: "ew-resize",
              zIndex: 10,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "none",
              background:
                isDragging === "end" ? "rgba(255,255,255,0.1)" : "transparent",
              borderRadius: "8px",
              transition: "background 0.2s ease",
            }}
            onMouseDown={handleMouseDown("end")}
            onTouchStart={handleTouchStart("end")}
          >
            <div
              style={{
                width: "4px",
                height: "12px",
                background: "#FFFFFF",
                borderRadius: "2px",
                boxShadow:
                  isDragging === "end"
                    ? "0 0 8px rgba(255, 255, 255, 0.8)"
                    : "0 0 2px rgba(0, 0, 0, 0.3)",
                transition: "box-shadow 0.1s ease",
              }}
            />
          </div>
        </>
      )}

      {/* Data type indicator */}
      <div
        style={{
          position: "absolute",
          top: "-20px",
          left: "8px",
          right: "60px",
          fontSize: "10px",
          color: dataColor,
          fontFamily: "Figtree, sans-serif",
          fontWeight: "500",
          textTransform: "uppercase",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {brushRange
          ? `${dataType.toUpperCase()} • ZOOMED`
          : `${dataType.toUpperCase()} • ${timeWindow / 1000}S`}
      </div>
    </div>
  );
};

// Live Display Buffer Management Functions
const createLiveBuffer = (windowSize: number): LiveDisplayBuffer => ({
  data: [],
  windowSize,
  lastUpdateTime: 0,
  currentCycle: 0,
});

const updateLiveBuffer = (
  buffer: LiveDisplayBuffer,
  newPoints: DataPoint[]
): LiveDisplayBuffer => {
  if (newPoints.length === 0) return buffer;

  const updatedBuffer = { ...buffer };

  newPoints.forEach((point) => {
    const displayTime = point.time % buffer.windowSize;
    const currentCycle = Math.floor(point.time / buffer.windowSize);

    // Remove points in sweep zone ahead of new point
    const sweepStart = displayTime;
    const sweepZoneSize = buffer.windowSize / 20; // 5% of window size
    const sweepEnd = (displayTime + sweepZoneSize) % buffer.windowSize;

    if (sweepEnd < sweepStart) {
      // Handle wrap-around case
      updatedBuffer.data = updatedBuffer.data.filter(
        (p) => !(p.displayTime >= sweepStart || p.displayTime <= sweepEnd)
      );
    } else {
      // Normal case
      updatedBuffer.data = updatedBuffer.data.filter(
        (p) => !(p.displayTime >= sweepStart && p.displayTime <= sweepEnd)
      );
    }

    // Proper recent/old classification - only current cycle points are recent
    if (currentCycle > updatedBuffer.currentCycle) {
      updatedBuffer.data = updatedBuffer.data.map((p) => ({
        ...p,
        isRecent: false,
      }));
    }
    // Add new point
    updatedBuffer.data.push({
      ...point,
      displayTime,
      isRecent: true, // New points are always recent
      cycleStartTime: currentCycle,
    });

    updatedBuffer.data = updatedBuffer.data.filter(
      (p) => !(p.displayTime <= displayTime && !p.isRecent)
    );

    updatedBuffer.currentCycle = Math.max(
      updatedBuffer.currentCycle,
      currentCycle
    );
  });
  return updatedBuffer;
};

// Calculate number of ticks based on available height to prevent overlap
const calculateNumTicks = (height: number, isMobile: boolean): number => {
  // Minimum 7 ticks (3 below zero, zero, 3 above zero)
  const minTicks = 7;

  if (isMobile) {
    if (height < 200) return minTicks; // 7 ticks
    if (height < 300) return 9; // 9 ticks (4 below, 0, 4 above)
    return 11; // 11 ticks (5 below, 0, 5 above)
  } else {
    if (height < 250) return minTicks; // 7 ticks
    if (height < 350) return 9; // 9 ticks
    if (height < 450) return 11; // 11 ticks
    return 13; // 13 ticks (6 below, 0, 6 above)
  }
};

// Modified Graph Component with live buffer support and segmentation for zoom mode only
/** Plot-area margins. Shared by the graph and the brush so they align. */
const getGraphMargin = (
  isMobile: boolean,
  graphCount: number,
  hasPlx: boolean
) => {
  const baseBottomMargin = isMobile ? 45 : 40;
  const additionalBottomMargin = isMobile && graphCount > 2 ? 10 : 0;
  const rightBase = isMobile ? 50 : 60;
  const right = hasPlx ? rightBase + 30 : rightBase; // reserve space for AxisRight & label

  return {
    top: 20,
    right,
    bottom: baseBottomMargin + additionalBottomMargin,
    left: isMobile ? 70 : 80,
  };
};

const RealtimeGraphComponent: React.FC<{
  data: DataPoint[];
  liveBuffer: LiveDisplayBuffer;
  graphType: GraphType;
  width: number;
  height: number;
  therapyState: boolean;
  brushRange: [number, number] | null;
  timeWindow: number;
  isMobile: boolean;
  graphCount: number;
  onExpand: (graphType: string) => void;
  showExpandIcon: boolean;
  hasPlx?: boolean;
  plxSpO2Buffer?: LiveDisplayBuffer;
  plxPulseRateBuffer?: LiveDisplayBuffer;
  plxSessionData?: { spO2Data: DataPoint[]; pulseRateData: DataPoint[] };
  plxMode?: "spO2" | "pulseRate";
}> = ({
  data,
  liveBuffer,
  graphType,
  width,
  height,
  therapyState,
  brushRange,
  timeWindow,
  isMobile,
  graphCount,
  onExpand,
  showExpandIcon,
  hasPlx = false,
  plxSpO2Buffer,
  plxPulseRateBuffer,
  plxSessionData,
  plxMode = "spO2",
}) => {
  // Responsive margins (shared with the brush via getGraphMargin)
  const margin = useMemo(
    () => getGraphMargin(isMobile, graphCount, hasPlx),
    [isMobile, graphCount, hasPlx]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // Determine which data to use and create filtered data
  const { filteredData, isLiveMode, xDomain } = useMemo(() => {
    if (brushRange) {
      // Zoom mode - use historical data with dynamic x-axis
      const filtered = data.filter(
        (d) => d.time >= brushRange[0] && d.time <= brushRange[1]
      );
      const times = filtered.map((d) => d.time);
      const minTime = times.length > 0 ? Math.min(...times) : brushRange[0];
      const maxTime = times.length > 0 ? Math.max(...times) : brushRange[1];

      return {
        filteredData: filtered,
        isLiveMode: false,
        xDomain: [minTime, maxTime],
      };
    } else {
      // Live mode - use live buffer with fixed x-axis (0 to timeWindow)
      const liveData = liveBuffer.data.map(
        (p): DataPoint & { isRecent?: boolean } => ({
          time: p.displayTime,
          value: p.value,
          isRecent: p.isRecent,
        })
      );

      return {
        filteredData: liveData,
        isLiveMode: true,
        xDomain: [0, timeWindow],
      };
    }
  }, [data, liveBuffer, brushRange, timeWindow]);

  // Calculate dynamic Y range
  const yRange = useMemo(() => {
    if (filteredData.length === 0) return graphType.range;

    const useFixedRange = graphType.useFixedRangeInLive !== false; // default true

    // LIVE MODE with fixed range enabled
    if (isLiveMode && useFixedRange) {
      const [defaultMin, defaultMax] = graphType.range;

      // Check if data exceeds default range
      const values = filteredData.map((d) => d.value);
      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);

      // Auto-expand if data exceeds limits
      const actualMin = dataMin < defaultMin ? dataMin : defaultMin;
      const actualMax = dataMax > defaultMax ? dataMax : defaultMax;

      return [actualMin, actualMax];
    }

    // ZOOM MODE or auto-scaling enabled - dynamic range with padding
    const values = filteredData.map((d) => d.value);
    const dataMin = Math.min(...values, 0);
    const dataMax = Math.max(...values, 0);
    const padding = Math.max((dataMax - dataMin) * 0.1, 1);

    return [dataMin - padding, dataMax + padding];
  }, [
    filteredData,
    graphType.range,
    graphType.useFixedRangeInLive,
    isLiveMode,
  ]);

  // Create scales
  const xScale = useMemo(() => {
    return scaleLinear({
      range: [0, xMax],
      domain: xDomain,
    });
  }, [xMax, xDomain]);

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: yRange,
      }),
    [yMax, yRange]
  );

  const plxFilteredData = useMemo(() => {
    if (!hasPlx || !plxMode) return [] as DataPoint[];

    if (brushRange) {
      // zoom mode: use session history array (absolute elapsed times)
      const arr =
        plxMode === "spO2"
          ? plxSessionData?.spO2Data ?? []
          : plxSessionData?.pulseRateData ?? [];
      return arr.filter(
        (d) => d.time >= brushRange[0] && d.time <= brushRange[1]
      );
    } else {
      // live mode: use the CORRECT buffer based on current mode
      const correctBuffer =
        plxMode === "spO2" ? plxSpO2Buffer : plxPulseRateBuffer;
      return (correctBuffer?.data ?? []).map((p) => ({
        time: p.displayTime,
        value: p.value,
        isRecent: p.isRecent,
      }));
    }
  }, [
    hasPlx,
    plxMode,
    plxSessionData,
    plxSpO2Buffer,
    plxPulseRateBuffer,
    brushRange,
  ]);

  // PLX y-range & scale (right axis)
  const plxYRange = useMemo(() => {
    if (!plxFilteredData || plxFilteredData.length === 0) {
      // fallback to fixed ranges
      return PLX_FIXED_RANGES[plxMode];
    }

    // LIVE MODE - use fixed ranges with auto-expansion
    if (isLiveMode) {
      const [defaultMin, defaultMax] = PLX_FIXED_RANGES[plxMode];

      // Check if data exceeds default range
      const vals = plxFilteredData.map((d) => d.value);
      const dataMin = Math.min(...vals);
      const dataMax = Math.max(...vals);

      // Auto-expand if data exceeds limits
      const actualMin = dataMin < defaultMin ? dataMin : defaultMin;
      const actualMax = dataMax > defaultMax ? dataMax : defaultMax;

      return [actualMin, actualMax];
    }

    // ZOOM MODE - dynamic scaling with padding
    const vals = plxFilteredData.map((d) => d.value);
    const minV = Math.min(...vals);
    const maxV = Math.max(...vals);
    const pad = Math.max((maxV - minV) * 0.1, 1);
    return [minV - pad, maxV + pad];
  }, [plxFilteredData, plxMode, isLiveMode]);

  const plxYScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: plxYRange,
      }),
    [yMax, plxYRange]
  );

  if (!therapyState && data.length === 0) {
    return (
      <div
        style={{
          width,
          height,
          background: "#1C1C1E",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            background: "rgba(28, 28, 30, 0.9)",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontFamily: "Figtree, sans-serif",
              fontSize: "16px",
              fontWeight: "600",
              color: "#FB0702",
              display: "block",
              marginBottom: "8px",
            }}
          >
            THERAPY STOPPED
          </span>
          <span
            style={{
              fontFamily: "Figtree, sans-serif",
              fontSize: "12px",
              color: "#B0B4B7",
            }}
          >
            Start therapy to view realtime curves
          </span>
        </div>
      </div>
    );
  }

  // Separate recent and old data for different rendering
  const recentData = isLiveMode
    ? filteredData.filter((d: any) => d.isRecent)
    : filteredData;
  const oldData = isLiveMode
    ? filteredData.filter((d: any) => !d.isRecent)
    : [];

  return (
    <div
      style={{
        width,
        height,
        background: "#1C1C1E",
        borderRadius: "8px",
        position: "relative",
        marginBottom: "8px",
      }}
    >
      {/* Expand Icon - Top Right */}
      {showExpandIcon && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => onExpand(graphType.type)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="30" height="30" rx="15" fill="#363636" />
              <path
                d="M20.8346 9.1665L15.8346 9.1665L15.8346 10.8332L19.168 10.8332L19.168 14.1665L20.8346 14.1665L20.8346 9.1665ZM14.168 19.1665L10.8346 19.1665L10.8346 15.8332L9.16797 15.8332L9.16797 20.8332L14.168 20.8332L14.168 19.1665Z"
                fill="#B0B4B7"
              />
            </svg>
          </button>
        </div>
      )}

      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          {/* Grid */}
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="2,2"
            stroke="#363636"
            strokeOpacity={0.6}
          />
          <GridColumns
            scale={xScale}
            height={yMax}
            strokeDasharray="2,2"
            stroke="#363636"
            strokeOpacity={0.3}
          />

          {/* Zero line */}
          {yRange[0] <= 0 && yRange[1] >= 0 && (
            <line
              x1={0}
              y1={yScale(0)}
              x2={xMax}
              y2={yScale(0)}
              stroke="#FFFFFF"
              strokeWidth={0.5}
              strokeOpacity={0.8}
              strokeDasharray="4,4"
            />
          )}

          {/* Old data line (dimmed) - only in live mode */}
          {isLiveMode && oldData.length > 0 && (
            <LinePath
              data={oldData}
              x={(d) => xScale(d.time)}
              y={(d) => yScale(d.value)}
              stroke={graphType.color}
              strokeWidth={2}
              strokeOpacity={2} // not Dimmed for old data
              curve={curveLinear}
            />
          )}

          {/* Recent data line with segmentation - apply segmentation only in zoom mode */}
          {recentData.length > 0 &&
            (() => {
              if (brushRange) {
                // Zoom mode - use segmentation
                const segments = segmentDataByGaps(recentData);
                return segments.map((segment, index) => (
                  <LinePath
                    key={`segment-${index}`}
                    data={segment}
                    x={(d) => xScale(d.time)}
                    y={(d) => yScale(d.value)}
                    stroke={graphType.color}
                    strokeWidth={2}
                    curve={curveLinear}
                  />
                ));
              } else {
                // Live mode - unchanged
                return (
                  <LinePath
                    data={recentData}
                    x={(d) => xScale(d.time)}
                    y={(d) => yScale(d.value)}
                    stroke={graphType.color}
                    strokeWidth={2}
                    curve={curveLinear}
                  />
                );
              }
            })()}

          {/* Data points for sparse data */}
          {/* Recent data points (full brightness) */}
          {recentData.length <= 50 &&
            recentData.map((point, index) => (
              <circle
                key={`recent-${index}`}
                cx={xScale(point.time)}
                cy={yScale(point.value)}
                r={1.5}
                fill={graphType.color}
              />
            ))}

          {/* Axes */}
          <AxisLeft
            scale={yScale}
            numTicks={calculateNumTicks(height, isMobile)}
            tickFormat={(value) => Number(value).toFixed(graphType.decimals)}
            stroke="#87868B"
            tickStroke="#87868B"
            tickLabelProps={() => ({
              fontSize: isMobile ? 10 : 11,
              fontFamily: "Figtree, sans-serif",
              fill: "#87868B",
              textAnchor: "end",
              dx: isMobile ? -8 : -12,
              dy: 4,
            })}
          />

          <AxisBottom
            top={yMax}
            scale={xScale}
            numTicks={isMobile ? 3 : 4}
            tickFormat={(value) => {
              if (isLiveMode) {
                // Live mode: show fixed scale from 0 to timeWindow
                const seconds = Number(value) / 1000;
                return `${seconds.toFixed(1)}s`;
              } else {
                // Zoom mode: show actual elapsed time
                const elapsedSeconds = Number(value) / 1000;
                return `${elapsedSeconds.toFixed(1)}s`;
              }
            }}
            stroke="#87868B"
            tickStroke="#87868B"
            tickLabelProps={() => ({
              fontSize: isMobile ? 10 : 11,
              fontFamily: "Figtree, sans-serif",
              fill: "#87868B",
              textAnchor: "middle",
              dy: isMobile ? 14 : 12,
            })}
          />

          {/* Right axis for PLX */}
          {hasPlx && plxFilteredData.length > 0 && (
            <>
              <AxisRight
                left={xMax}
                scale={plxYScale}
                numTicks={Math.min(
                  Math.abs(
                    Math.round(plxYRange[1]) - Math.round(plxYRange[0])
                  ) + 1,
                  isMobile ? 11 : 13
                )}
                tickFormat={(v) => Math.round(Number(v)).toString()}
                stroke="#87868B"
                tickStroke="#87868B"
                tickLabelProps={() => ({
                  fontSize: isMobile ? 10 : 11,
                  fontFamily: "Figtree, sans-serif",
                  fill: "#87868B",
                  textAnchor: "start",
                  dx: 8,
                  dy: 4,
                })}
                tickValues={(() => {
                  // Only show ticks that are within the scale's domain (visible area)
                  const min = Math.ceil(
                    Math.max(plxYRange[0], plxYScale.domain()[0])
                  );
                  const max = Math.floor(
                    Math.min(plxYRange[1], plxYScale.domain()[1])
                  );
                  const maxTicks = isMobile ? 11 : 13;
                  const range = max - min;
                  if (range + 1 <= maxTicks) {
                    // Show all ticks within the visible domain
                    return Array.from({ length: range + 1 }, (_, i) => min + i);
                  } else {
                    // Show every other tick, but never skip two in a row
                    const ticks: number[] = [];
                    let skip = false;
                    for (let v = min; v <= max; v++) {
                      if (!skip) {
                        ticks.push(v);
                        skip = true;
                      } else {
                        skip = false;
                      }
                    }
                    // If we still have too many, spread evenly
                    if (ticks.length > maxTicks) {
                      const spreadTicks: number[] = [];
                      for (let i = 0; i < maxTicks; i++) {
                        const val = Math.round(
                          min + (i * range) / (maxTicks - 1)
                        );
                        if (!spreadTicks.includes(val)) spreadTicks.push(val);
                      }
                      return spreadTicks;
                    }
                    return ticks;
                  }
                })()}
              />

              {/* PLX line (connected straight) */}
              {isLiveMode ? (
                segmentPlxLiveDataByRecency(plxFilteredData as any).map(
                  (segment, idx) => (
                    <LinePath
                      key={`plx-live-seg-${idx}`}
                      data={segment}
                      x={(d) => xScale(d.time)}
                      y={(d) => plxYScale(Math.round(d.value))}
                      stroke={PLX_CONFIG[plxMode].color}
                      strokeWidth={2}
                      strokeOpacity={0.95}
                      curve={curveLinear}
                    />
                  )
                )
              ) : (
                <LinePath
                  data={plxFilteredData}
                  x={(d) => xScale(d.time)}
                  y={(d) => plxYScale(Math.round(d.value))}
                  stroke={PLX_CONFIG[plxMode].color}
                  strokeWidth={2}
                  strokeOpacity={0.95}
                  curve={curveLinear}
                />
              )}

              {/* PLX point markers (important because samples are sparse) */}
              {plxFilteredData.map((p, i) => (
                <circle
                  key={`plx-point-${i}`}
                  cx={xScale(p.time)}
                  cy={plxYScale(Math.round(p.value))}
                  r={isLiveMode ? 3.5 : 2.5}
                  fill={PLX_CONFIG[plxMode!].color}
                  stroke="#000"
                  strokeWidth={0.5}
                />
              ))}
            </>
          )}
        </Group>

        {/* Labels */}
        <text
          x={margin.left - (isMobile ? 8 : 12)}
          y={15}
          fill="#87868B"
          fontSize={isMobile ? "10px" : "11px"}
          fontWeight="400"
          fontFamily="Figtree"
          textAnchor="end"
        >
          {graphType.unit}
        </text>

        <text
          x={width - (showExpandIcon ? 120 : 84)}
          y={15}
          fill="#87868B"
          fontSize={isMobile ? "9px" : "10px"}
          fontFamily="Figtree"
          textAnchor="end"
        >
          {graphType.type.toUpperCase()}{" "}
          {isLiveMode ? `• LIVE ${timeWindow / 1000}S` : "• ZOOMED"}
        </text>

        {hasPlx && (
          <text
            x={width - (showExpandIcon ? 40 : 24)}
            y={15}
            fill={PLX_CONFIG[plxMode].color}
            fontSize={isMobile ? "9px" : "10px"}
            fontFamily="Figtree"
            textAnchor="end"
          >
            {PLX_CONFIG[plxMode].label}
          </text>
        )}
      </svg>
    </div>
  );
};

// PLX Mode Selector Component
const PlxModeSelector: React.FC<{
  selectedMode: "spO2" | "pulseRate";
  onModeChange: (mode: "spO2" | "pulseRate") => void;
  disabled?: boolean;
}> = ({ selectedMode, onModeChange, disabled = false }) => {
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      <CustomCheckbox
        label="SpO₂"
        checked={selectedMode === "spO2"}
        onChange={() => onModeChange("spO2")}
        disabled={disabled}
        primaryColor={PLX_CONFIG.spO2.color}
        secondaryColor={PLX_CONFIG.spO2.color}
        uncheckedColor="#B0B4B7"
      />
      <CustomCheckbox
        label="Pulse Rate"
        checked={selectedMode === "pulseRate"}
        onChange={() => onModeChange("pulseRate")}
        disabled={disabled}
        primaryColor={PLX_CONFIG.pulseRate.color}
        secondaryColor={PLX_CONFIG.pulseRate.color}
        uncheckedColor="#B0B4B7"
      />
    </div>
  );
};

// Main Component
const CurvesScreen: React.FC = () => {
  const {
    isConnected,
    realtimeData,
    realtimeDataPoints,
    isTherapyRunning,
    therapyState,
    serialNumber,
    askForRealTimeCurves,
    askForSelectiveData,
    pulseOximeterDevice,
    isPulseOximeterConnected,
    pulseOximeterSessionDataPoint,
    pulseOximeterSessionData,
  } = useBLE();
  const router = useIonRouter();

  // Landscape mode state
  const [forcedLandscape, setForcedLandscape] = useState(false);
  const [selectedLandscapeGraph, setSelectedLandscapeGraph] = useState<
    string | null
  >(null);
  const [preLandscapeGraphState, setPreLandscapeGraphState] = useState<
    GraphType[]
  >([]);

  const isFullScreenGraph = forcedLandscape;

  const [plxMode, setPlxMode] = useState<"spO2" | "pulseRate">("spO2");

  // Graph types state
  const [graphTypes, setGraphTypes] = useState<GraphType[]>([
    {
      type: "flow",
      enabled: true,
      color: "#fbfb24",
      unit: "LPM",
      range: [-400, 400],
      decimals: 1,
      useFixedRangeInLive: true, // Use fixed range in live mode
    },
    {
      type: "pressure",
      enabled: true,
      color: "#22D3EE",
      unit: "cm H2O",
      range: [-100, 100],
      decimals: 1,
      useFixedRangeInLive: true, // Use fixed range in live mode
    },
    {
      type: "volume",
      enabled: true,
      color: "#f73434",
      unit: "mL",
      range: [-2000, 2000],
      decimals: 1,
      useFixedRangeInLive: true, // Use fixed range in live mode
    },
  ]);

  // Time window state
  const [timeWindow, setTimeWindow] = useState<number>(12000); // Default 12s

  // Brush state - null means no selection (use sliding window)
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);

  // Transition state to ensure smooth transitions
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Live display buffers
  const [liveBuffers, setLiveBuffers] = useState({
    flow: createLiveBuffer(12000),
    pressure: createLiveBuffer(12000),
    volume: createLiveBuffer(12000),
    plxSpO2: createLiveBuffer(12000),
    plxPulseRate: createLiveBuffer(12000),
  });

  // FIXED: Clear buffers when therapy stops or component unmounts
  useEffect(() => {
    if (!isTherapyRunning) {
      setLiveBuffers({
        flow: createLiveBuffer(timeWindow),
        pressure: createLiveBuffer(timeWindow),
        volume: createLiveBuffer(timeWindow),
        plxSpO2: createLiveBuffer(timeWindow),
        plxPulseRate: createLiveBuffer(timeWindow),
      });
    }
  }, [isTherapyRunning, timeWindow]);

  // FIXED: Clear buffers on component unmount
  useEffect(() => {
    return () => {
      setLiveBuffers({
        flow: createLiveBuffer(timeWindow),
        pressure: createLiveBuffer(timeWindow),
        volume: createLiveBuffer(timeWindow),
        plxSpO2: createLiveBuffer(timeWindow),
        plxPulseRate: createLiveBuffer(timeWindow),
      });
    };
  }, [timeWindow]);

  // Update live buffers when new data arrives
  useEffect(() => {
    if (isTherapyRunning) {
      setLiveBuffers((prev) => ({
        flow: updateLiveBuffer(
          { ...prev.flow, windowSize: timeWindow },
          realtimeDataPoints.flowData
        ),
        pressure: updateLiveBuffer(
          { ...prev.pressure, windowSize: timeWindow },
          realtimeDataPoints.pressureData
        ),
        volume: updateLiveBuffer(
          { ...prev.volume, windowSize: timeWindow },
          realtimeDataPoints.volumeData
        ),
        plxSpO2: { ...prev.plxSpO2, windowSize: timeWindow },
        plxPulseRate: { ...prev.plxPulseRate, windowSize: timeWindow },
      }));
    }
  }, [realtimeDataPoints, timeWindow, isTherapyRunning]);

  // push incoming PLX data (once per second) into the PLX live buffer
  useEffect(() => {
    if (!isPulseOximeterConnected || !pulseOximeterSessionDataPoint) return;

    // Store BOTH data types simultaneously
    const spO2Point = pulseOximeterSessionDataPoint.spO2Data;
    const pulseRatePoint = pulseOximeterSessionDataPoint.pulseRateData;

    setLiveBuffers((prev) => ({
      ...prev,
      plxSpO2: spO2Point
        ? updateLiveBuffer({ ...prev.plxSpO2, windowSize: timeWindow }, [
            spO2Point,
          ])
        : prev.plxSpO2,
      plxPulseRate: pulseRatePoint
        ? updateLiveBuffer({ ...prev.plxPulseRate, windowSize: timeWindow }, [
            pulseRatePoint,
          ])
        : prev.plxPulseRate,
    }));
  }, [pulseOximeterSessionDataPoint, isPulseOximeterConnected, timeWindow]);

  // Calculate all-time range for brush
  const allTimeRange = useMemo((): [number, number] => {
    // console.log("Calculating allTimeRange", timeWindow);
    const allData = [
      ...realtimeData.pressureData,
      ...realtimeData.flowData,
      ...realtimeData.volumeData,
    ];

    if (allData.length === 0) {
      const now = Date.now();
      return [now - timeWindow, now];
    }

    const times = allData.map((d) => d.time);
    return [Math.min(...times), Math.max(...times)];
  }, [realtimeData, timeWindow]);

  // Dynamic bottom spacing based on viewport dimensions
  const [bottomSpacing, setBottomSpacing] = useState(16);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  // Web: scales the brush/footer bar with viewport height.
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // Measured graph area — window.innerWidth ignores the SideNav.
  const graphAreaRef = useRef<HTMLDivElement>(null);
  const [graphAreaWidth, setGraphAreaWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = graphAreaRef.current;
    if (!el) return;
    const measure = () => setGraphAreaWidth(el.clientWidth);
    measure();
    // Observer, not window resize — catches the SideNav collapsing.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Web: measure the real header/controls height instead of guessing.
  const headerRef = useRef<HTMLIonHeaderElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState<
    number | null
  >(null);
  const [measuredControlsHeight, setMeasuredControlsHeight] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!IS_WEB_BUILD) return;
    const headerEl = headerRef.current;
    const controlsEl = controlsRef.current;
    const observer = new ResizeObserver((entries) => {
      // getBoundingClientRect includes padding; entry.contentRect doesn't.
      for (const entry of entries) {
        const height = entry.target.getBoundingClientRect().height;
        if (entry.target === headerEl) {
          setMeasuredHeaderHeight(height);
        } else if (entry.target === controlsEl) {
          setMeasuredControlsHeight(height);
        }
      }
    });
    if (headerEl) observer.observe(headerEl);
    if (controlsEl) observer.observe(controlsEl);
    return () => observer.disconnect();
  }, [isFullScreenGraph]);

  const [quickTransitionSupport, setQuickTransitionSupport] =
    useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isTherapyRunning) {
      intervalRef.current = setInterval(() => {
        setQuickTransitionSupport((prev) => prev + 1);
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isTherapyRunning]);

  useEffect(() => {
    const calculateBottomSpacing = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      let calculatedSpacing = 16;

      if (isMobile) {
        if (vh < 700) {
          calculatedSpacing = Math.max(12, vh * 0.02);
        } else if (vh < 800) {
          calculatedSpacing = Math.max(16, vh * 0.025);
        } else if (vh < 900) {
          calculatedSpacing = Math.max(18, vh * 0.028);
        } else {
          calculatedSpacing = Math.max(20, vh * 0.03);
        }

        const aspectRatio = Math.max(vw, vh) / Math.min(vw, vh);
        if (aspectRatio > 2.0 && vh > 750) {
          calculatedSpacing += Math.min(12, vh * 0.015);
        }
      }

      setBottomSpacing(Math.round(calculatedSpacing));
      setIsMobile(window.innerWidth < 480);
      // Web only.
      if (IS_WEB_BUILD) setViewportHeight(window.innerHeight);
    };

    calculateBottomSpacing();
    window.addEventListener("resize", calculateBottomSpacing);
    window.addEventListener("orientationchange", () => {
      setTimeout(calculateBottomSpacing, 100);
    });

    return () => {
      window.removeEventListener("resize", calculateBottomSpacing);
      window.removeEventListener("orientationchange", calculateBottomSpacing);
    };
  }, [isMobile, forcedLandscape]);

  // Force landscape orientation for specific graph
  const forceEnterLandscape = async (graphType: string) => {
    setIsTransitioning(true);
    setPreLandscapeGraphState([...graphTypes]);
    setSelectedLandscapeGraph(graphType);
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "landscape" });
      } catch (error) {
        console.warn("Failed to force landscape:", error);
      }
    }
    setForcedLandscape(true);
    setIsTransitioning(false);
  };

  // Exit landscape and return to portrait
  const exitLandscape = async () => {
    setIsTransitioning(true);
    setSelectedLandscapeGraph(null);
    if (preLandscapeGraphState.length > 0) {
      setGraphTypes([...preLandscapeGraphState]);
    }
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch (error) {
        console.warn("Failed to return to portrait:", error);
      }
    }
    setForcedLandscape(false);
    setIsTransitioning(false);
  };

  useEffect(() => {
    const lockOrientation = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await ScreenOrientation.lock({ orientation: "portrait" });
        } catch (error) {
          console.warn("Failed to lock orientation:", error);
        }
      }
    };
    lockOrientation();
  }, []);

  // Auto-return to portrait when navigating away
  const handleNavigation = async () => {
    if (isFullScreenGraph) {
      await exitLandscape();
      setTimeout(() => router.push("/remote"), 100);
    } else {
      router.push("/remote");
    }
  };

  useEffect(() => {
    console.log("Graph types changed:", isTransitioning);
  }, [isTransitioning]);

  // Initialize data requests
  useEffect(() => {
    if (isConnected) {
      askForSelectiveData([
        RequestMessageType.REALTIME_CURVES,
        RequestMessageType.MAPS_FOR_LOGS,
      ]);
      askForRealTimeCurves(true, true, true, 100);// changed for reducing load on device
    }
  }, [isConnected, therapyState]);

  useEffect(() => {
    if (!isTherapyRunning) {
      if (
        realtimeData.flowData.length === 0 &&
        realtimeData.pressureData.length === 0 &&
        realtimeData.volumeData.length === 0
      ) {
        setBrushRange(null);
      } else {
        setBrushRange(allTimeRange);
      }
    }
  }, [isTherapyRunning]);

  const toggleGraph = (type: string) => {
    setGraphTypes((prev) =>
      prev.map((graph) =>
        graph.type === type ? { ...graph, enabled: !graph.enabled } : graph
      )
    );
  };

  const handleTimeWindowChange = (newWindow: number) => {
    setTimeWindow(newWindow);
    setLiveBuffers((prev) => ({
      flow: { ...prev.flow, windowSize: newWindow },
      pressure: { ...prev.pressure, windowSize: newWindow },
      volume: { ...prev.volume, windowSize: newWindow },
      plxSpO2: { ...prev.plxSpO2, windowSize: newWindow },
      plxPulseRate: { ...prev.plxPulseRate, windowSize: newWindow },
    }));
  };

  const enabledGraphs = graphTypes.filter((graph) => graph.enabled);

  // Dynamic layout calculations. Web: prefer measured height over fixed guesses.
  const headerHeight = isFullScreenGraph
    ? 0
    : IS_WEB_BUILD && measuredHeaderHeight != null
    ? measuredHeaderHeight
    : 120;
  const fallbackControlsHeight =
    80 + (!isFullScreenGraph && isPulseOximeterConnected ? 32 : 0);
  const controlsHeight = isFullScreenGraph
    ? 0
    : IS_WEB_BUILD && measuredControlsHeight != null
    ? measuredControlsHeight
    : fallbackControlsHeight;
  // Web: brush/footer bar scales with viewport height, clamped 64-80px.
  const clamp = (min: number, val: number, max: number) =>
    Math.min(max, Math.max(min, val));
  const baseBrushHeight = IS_WEB_BUILD
    ? clamp(64, viewportHeight * 0.1, 80)
    : 80;
  const brushPadding = IS_WEB_BUILD ? clamp(6, viewportHeight * 0.02, 16) : 16;
  // Web: reuse brushPadding — bottomSpacing doesn't scale with height.
  const footerBottomSpacing = IS_WEB_BUILD ? brushPadding : bottomSpacing;
  const totalBrushHeight = baseBrushHeight + footerBottomSpacing;
  const availableHeight =
    window.innerHeight - headerHeight - controlsHeight - totalBrushHeight;

  // Space for "Switch to Live". Always reserved so width never changes.
  const SWITCH_TO_LIVE_RESERVE = 36;

  const getAvailableWidth = () => {
    // Measured container; window is the first-render fallback.
    const screenWidth = graphAreaWidth ?? window.innerWidth;

    // Same rule for both modes. Raise to narrow the graph.
    const GRAPH_INSET = 64;
    // Clamp: a 0-width container (first measure) would go negative.
    return Math.max(0, screenWidth - GRAPH_INSET - SWITCH_TO_LIVE_RESERVE);
  };

  const availableWidth = getAvailableWidth();

  const getMinGraphHeight = () => {
    if (isMobile && enabledGraphs.length > 2) return 160;
    if (isMobile && enabledGraphs.length > 1) return 140;
    return 120;
  };

  // Single source of truth — passed to the graph and used for the brush margins,
  // so the two can never drift out of alignment.
  const hasPlx =
    isPulseOximeterConnected &&
    (plxMode === "pulseRate"
      ? pulseOximeterSessionData?.pulseRateData?.length > 0
      : pulseOximeterSessionData?.spO2Data?.length > 0);
  const brushMargin = getGraphMargin(isMobile, enabledGraphs.length, hasPlx);
  const brushWidth = Math.max(
    0,
    availableWidth - brushMargin.left - brushMargin.right
  );

  const minGraphHeight = getMinGraphHeight();
  const graphHeight = Math.max(
    minGraphHeight,
    (availableHeight - 20) / Math.max(enabledGraphs.length, 1)
  );

  const graphWidth = availableWidth;

  if (isTransitioning) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          position: "fixed",
          top: 0,
          left: 0,
          transition: "opacity 0.3s",
          opacity: 1,
        }}
      ></div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-black text-white ${
        isPlatform("android") ? "pt-[20px]" : ""
      }`}
    >
      {/* Conditional Header - Only show in portrait */}
      {/* Enhanced Header with PLX Status - Only show in portrait */}
      {!isFullScreenGraph && (
        <IonHeader ref={headerRef}>
          <IonToolbar
            style={{
              "--background": "#1C1C1E",
              "--border-color": "rgba(255, 255, 255, 0.1)",
              "--color": "#FFFFFF",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 16px",
                minHeight: "64px",
              }}
            >
              <div>
                <IonTitle
                  style={{
                    fontFamily: "Figtree, sans-serif",
                    fontWeight: "700",
                    fontSize: "20px",
                    color: "#FFFFFF",
                    margin: 0,
                  }}
                >
                  Live Therapy Curves
                </IonTitle>

                {/* Enhanced Device Connection Status */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    marginTop: "4px",
                  }}
                >
                  {/* Therapy Device Status */}
                  {isConnected && serialNumber && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "#00FFFF", // Cyan for therapy device
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "Figtree, sans-serif",
                          fontSize: "12px",
                          color: "#B0B4B7",
                        }}
                      >
                        Connected • {serialNumber}
                      </span>
                    </div>
                  )}

                  {/* Pulse Oximeter Status */}
                  {isPulseOximeterConnected && pulseOximeterDevice && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "#00FF88", // Green for pulse oximeter
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "Figtree, sans-serif",
                          fontSize: "12px",
                          color: "#B0B4B7",
                        }}
                      >
                        PLX •{" "}
                        {pulseOximeterDevice.device.name || "Pulse Oximeter"}
                      </span>
                    </div>
                  )}

                  {/* No Devices Connected */}
                  {!isConnected && !isPulseOximeterConnected && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "#FB0702", // Red for disconnected
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "Figtree, sans-serif",
                          fontSize: "12px",
                          color: "#B0B4B7",
                        }}
                      >
                        Disconnected
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <IonButtons slot="end">
                <IonButton
                  fill="clear"
                  onClick={handleNavigation}
                  style={{ "--color": "#B0B4B7" }}
                >
                  <IonIcon
                    icon={closeOutline}
                    style={{ width: "24px", height: "24px" }}
                  />
                </IonButton>
              </IonButtons>
            </div>
          </IonToolbar>
        </IonHeader>
      )}

      {/* Three Control Rows - Only in Portrait */}
      {!isFullScreenGraph && (
        <div
          ref={controlsRef}
          style={{
            // Fixed value, not controlsHeight — that's measured from this
            // element, so using it here would self-reference.
            minHeight: `${fallbackControlsHeight}px`,
            padding: "0 16px",
            background: "#000000",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
              minHeight: "24px",
            }}
          >
            <span
              style={{
                fontFamily: "Figtree, sans-serif",
                fontSize: "13px",
                fontWeight: "600",
                color: "#FFFFFF",
                marginRight: "12px",
                minWidth: "80px",
              }}
            >
              Graph Types:
            </span>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {graphTypes.map((graph) => (
                <CustomCheckbox
                  key={graph.type}
                  label={
                    graph.type.charAt(0).toUpperCase() + graph.type.slice(1)
                  }
                  checked={graph.enabled}
                  onChange={() => toggleGraph(graph.type)}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: "24px",
            }}
          >
            <span
              style={{
                fontFamily: "Figtree, sans-serif",
                fontSize: "13px",
                fontWeight: "600",
                color: "#FFFFFF",
                marginRight: "12px",
                minWidth: "80px",
              }}
            >
              X-Scale:
            </span>
            <TimeWindowSelector
              selectedWindow={timeWindow}
              onWindowChange={handleTimeWindowChange}
            />
          </div>

          {/* ADD NEW PLX Controls Row */}
          {isPulseOximeterConnected && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: "24px",
              }}
            >
              <span
                style={{
                  fontFamily: "Figtree, sans-serif",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  marginRight: "10px",
                  minWidth: "80px",
                }}
              >
                Pulse Oximetry:
              </span>
              <PlxModeSelector
                selectedMode={plxMode}
                onModeChange={setPlxMode}
                disabled={!isPulseOximeterConnected}
              />
            </div>
          )}
        </div>
      )}

      {/* Graphs - Different rendering for landscape and portrait */}
      <div
        ref={graphAreaRef}
        style={{
          padding: "16px",
          background: "#000000",
          height: `${availableHeight}px`,
          overflow: isFullScreenGraph ? "hidden" : "auto",
        }}
      >
        {isFullScreenGraph && selectedLandscapeGraph ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              justifyContent: "flex-start",
              maxWidth: "100%",
              height: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                marginBottom: "8px",
                minHeight: "30px",
              }}
            >
              <button
                onClick={exitLandscape}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="#B0B4B7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {(() => {
              const selectedGraph = graphTypes.find(
                (g) => g.type === selectedLandscapeGraph
              );
              if (!selectedGraph) return null;

              const graphData =
                selectedLandscapeGraph === "flow"
                  ? realtimeData.flowData
                  : selectedLandscapeGraph === "pressure"
                  ? realtimeData.pressureData
                  : realtimeData.volumeData;

              const graphLiveBuffer =
                selectedLandscapeGraph === "flow"
                  ? liveBuffers.flow
                  : selectedLandscapeGraph === "pressure"
                  ? liveBuffers.pressure
                  : liveBuffers.volume;

              const landscapeGraphHeight = Math.max(
                minGraphHeight,
                availableHeight - 38 - (isMobile ? 25 : 20)
              );

              return (
                <div style={{ flex: 1 }}>
                  <RealtimeGraphComponent
                    key={selectedGraph.type}
                    data={graphData}
                    liveBuffer={graphLiveBuffer}
                    graphType={selectedGraph}
                    width={graphWidth}
                    height={landscapeGraphHeight}
                    therapyState={isTherapyRunning}
                    brushRange={brushRange}
                    timeWindow={timeWindow}
                    isMobile={isMobile}
                    graphCount={1}
                    onExpand={() => {
                      console.log("Expand graph:", selectedGraph.type);
                    }}
                    showExpandIcon={false}
                    hasPlx={hasPlx}
                    plxSpO2Buffer={liveBuffers.plxSpO2}
                    plxPulseRateBuffer={liveBuffers.plxPulseRate}
                    plxSessionData={pulseOximeterSessionData} // full session object (choose array inside component)
                    plxMode={plxMode}
                  />
                </div>
              );
            })()}
          </div>
        ) : enabledGraphs.length > 0 && !isTransitioning ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              alignItems: "stretch",
              justifyContent: "flex-start",
              maxWidth: "100%",
            }}
          >
            {enabledGraphs.map((graph) => {
              const graphData =
                graph.type === "flow"
                  ? realtimeData.flowData
                  : graph.type === "pressure"
                  ? realtimeData.pressureData
                  : realtimeData.volumeData;

              const graphLiveBuffer =
                graph.type === "flow"
                  ? liveBuffers.flow
                  : graph.type === "pressure"
                  ? liveBuffers.pressure
                  : liveBuffers.volume;

              return (
                <RealtimeGraphComponent
                  key={graph.type}
                  data={graphData}
                  liveBuffer={graphLiveBuffer}
                  graphType={graph}
                  width={graphWidth}
                  height={graphHeight}
                  therapyState={isTherapyRunning}
                  brushRange={brushRange}
                  timeWindow={timeWindow}
                  isMobile={isMobile}
                  graphCount={enabledGraphs.length}
                  onExpand={forceEnterLandscape}
                  showExpandIcon={true}
                  hasPlx={hasPlx}
                  plxSpO2Buffer={liveBuffers.plxSpO2}
                  plxPulseRateBuffer={liveBuffers.plxPulseRate}
                  plxSessionData={pulseOximeterSessionData} // full session object (choose array inside component)
                  plxMode={plxMode}
                />
              );
            })}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              color: "#B0B4B7",
              fontFamily: "Figtree, sans-serif",
            }}
          >
            Select at least one graph type to view data
          </div>
        )}
      </div>

      {/* Dynamic brush component */}
      <div
        style={{
          height: `${totalBrushHeight}px`,
          padding: `${brushPadding}px`,
          paddingBottom: `${footerBottomSpacing}px`,
          background: "#000000",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {(() => {
          let brushData = realtimeData.pressureData;
          let brushDataType = "pressure";
          let brushColor = "#22D3EE";

          if (isFullScreenGraph && selectedLandscapeGraph) {
            if (selectedLandscapeGraph === "flow") {
              brushData = realtimeData.flowData;
              brushDataType = "flow";
              brushColor = "#fbfb24";
            } else if (selectedLandscapeGraph === "volume") {
              brushData = realtimeData.volumeData;
              brushDataType = "volume";
              brushColor = "#f73434";
            }
          }

          return (
            <BrushComponent
              data={brushData}
              brushRange={brushRange}
              onBrushChange={setBrushRange}
              allTimeRange={allTimeRange}
              timeWindow={timeWindow}
              dataType={brushDataType}
              dataColor={brushColor}
              width={brushWidth}
              offsetLeft={brushMargin.left}
            />
          );
        })()}

        {/* Follow Live Data Button */}
        {brushRange && isTherapyRunning && (
          <button
            onClick={() => setBrushRange(null)}
            style={{
              // Fits the gap made by SWITCH_TO_LIVE_RESERVE.
              marginLeft: "8px",
              padding: "8px 12px",
              background: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
              border: "none",
              borderRadius: "6px",
              color: "#FFFFFF",
              fontFamily: "Figtree, sans-serif",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: "600",
              lineHeight: 1.2,
              textAlign: "center",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "opacity 0.2s ease",
              minHeight: "36px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            Switch to Live
          </button>
        )}
      </div>
    </div>
  );
};

export default CurvesScreen;
